
import dayjs, { Dayjs } from "dayjs";
import { useState, useEffect } from "react";
import { RadioGroup, Autocomplete, TextField, Chip, CircularProgress } from "@mui/material";
import CustomRadioButton from "components/RadioButton";
import { Drawer } from "components/Drawer";
import { DateRangePicker } from "components/DateRangePicker";
import { FilterSection, InputSection, CustomDateSection, SectionTitle, CheckboxSection } from "./styles";
import CustomCheckBox, { ICheckboxOption } from "components/checkbox";
import { physicalTransshipService } from "services/physicalTransshipService";

type DateRange<T> = [T | null, T | null];

interface IBoxReturnFilterProps {
  open: boolean;
  setOpen: (x: boolean) => void;
  onChangeParams: (x: any) => void;
  checkboxes?: ICheckboxOption[];
  loading: boolean;
  initialParams?: {
    startDeliveryDate?: string;
    endDeliveryDate?: string;
    includeSupportDrivers?: boolean;
    physicalTransshipIds?: number[];
  };
  onCheckboxChange?: (key: string, value: boolean) => void;
}

const today = new Date();
const last7Days = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7);
const last15Days = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 15);
const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());

const formatDate = (date: Date) => dayjs(date).format("YYYY-MM-DD");

const initialEntries = [
  { id: 1, name: "Hoje", query: formatDate(today) },
  { id: 2, name: "Últimos 7 dias", query: formatDate(last7Days) },
  { id: 3, name: "Últimos 15 dias", query: formatDate(last15Days) },
  { id: 4, name: "Último mês", query: formatDate(lastMonth) },
  { id: 5, name: "Período customizado", query: "custom" },
];

export function FilterBoxesReturn({ open, setOpen, onChangeParams, checkboxes, loading, initialParams, onCheckboxChange }: IBoxReturnFilterProps) {
  interface PhysicalTransship {
    id: number;
    name: string;
    initials?: string | null;
    code?: string | null;
  }

  // Determina qual período predefinido está sendo usado
  const getInitialSelectedDate = () => {
    if (!initialParams?.startDeliveryDate || !initialParams?.endDeliveryDate) {
      return formatDate(today); // Default para "Hoje"
    }

    const startDate = initialParams.startDeliveryDate;
    const endDate = initialParams.endDeliveryDate;
    const todayFormatted = formatDate(today);

    // Se é um período customizado (startDate != endDate e endDate != hoje)
    if (startDate !== endDate && endDate !== todayFormatted) {
      return "custom";
    }

    // Verifica se corresponde a algum período predefinido
    const matchingEntry = initialEntries.find(entry => entry.query === startDate);
    return matchingEntry ? matchingEntry.query : "custom";
  };

  // Determina o dateRange inicial se for customizado
  const getInitialDateRange = (): DateRange<Dayjs> => {
    if (!initialParams?.startDeliveryDate || !initialParams?.endDeliveryDate || getInitialSelectedDate() !== "custom") {
      return [null, null];
    }

    try {
      const startDayjs = dayjs(initialParams.startDeliveryDate);
      const endDayjs = dayjs(initialParams.endDeliveryDate);
      return [startDayjs, endDayjs];
    } catch {
      return [null, null];
    }
  };

  const [selectedDate, setSelectedDate] = useState<string>(() => getInitialSelectedDate());
  const [dateRange, setDateRange] = useState<DateRange<Dayjs>>(() => getInitialDateRange());
  const [selectedPhysicalTransships, setSelectedPhysicalTransships] = useState<number[]>(() => initialParams?.physicalTransshipIds || []);
  const [localCheckboxStates, setLocalCheckboxStates] = useState<{ [key: string]: boolean }>({});
  const [pendingParams, setPendingParams] = useState<any>(initialParams || {});
  const [physicalOptions, setPhysicalOptions] = useState<PhysicalTransship[]>([]);
  const [selectedPhysicalOptions, setSelectedPhysicalOptions] = useState<PhysicalTransship[]>([]);
  const [internalLoading, setInternalLoading] = useState(false);

  // Função para determinar qual período predefinido está sendo usado
  const getUpdatedSelectedDate = () => {
    if (!initialParams?.startDeliveryDate || !initialParams?.endDeliveryDate) {
      return formatDate(today); // Default para "Hoje"
    }

    const startDate = initialParams.startDeliveryDate;
    const endDate = initialParams.endDeliveryDate;
    const todayFormatted = formatDate(today);

    // Se é um período customizado (startDate != endDate e endDate != hoje)
    if (startDate !== endDate && endDate !== todayFormatted) {
      return "custom";
    }

    // Verifica se corresponde a algum período predefinido
    const matchingEntry = initialEntries.find(entry => entry.query === startDate);
    return matchingEntry ? matchingEntry.query : "custom";
  };

  // Função para determinar o dateRange se for customizado
  const getUpdatedDateRange = (): DateRange<Dayjs> => {
    if (!initialParams?.startDeliveryDate || !initialParams?.endDeliveryDate) {
      return [null, null];
    }

    try {
      const startDayjs = dayjs(initialParams.startDeliveryDate);
      const endDayjs = dayjs(initialParams.endDeliveryDate);
      return [startDayjs, endDayjs];
    } catch {
      return [null, null];
    }
  };

  // Função para sincronizar todos os estados internos
  const syncStatesFromParams = () => {
    if (!initialParams) return;

    const newSelectedDate = getUpdatedSelectedDate();
    setSelectedDate(newSelectedDate);
    setDateRange(getUpdatedDateRange());
    setSelectedPhysicalTransships(initialParams.physicalTransshipIds || []);
    setPendingParams(initialParams);
  };

  // Inicializa estados locais dos checkboxes
  useEffect(() => {
    if (checkboxes) {
      const initialStates: { [key: string]: boolean } = {};
      checkboxes.forEach(checkbox => {
        initialStates[checkbox.id] = checkbox.state;
      });
      setLocalCheckboxStates(initialStates);
    }
  }, [checkboxes]);

  // Sincroniza estados internos quando initialParams mudam
  useEffect(() => {
    syncStatesFromParams();
  }, [initialParams]);

  // Busca lista de transbordos físicos
  useEffect(() => {
    const fetchPhysicalTransships = async () => {
      try {
        setInternalLoading(true);
        const response = await physicalTransshipService.getList({ pageSize: 1000, inUse: 1 });
        if (response && response.results) {
          setPhysicalOptions(response.results as unknown as PhysicalTransship[]);
        }
      } catch (e) {
        console.error("Erro ao buscar transbordos físicos:", e);
      } finally {
        setInternalLoading(false);
      }
    };
    fetchPhysicalTransships();
  }, []);

  // Sincroniza opções selecionadas a partir dos IDs
  useEffect(() => {
    if (selectedPhysicalTransships.length > 0 && physicalOptions.length > 0) {
      const selected = physicalOptions.filter(o => selectedPhysicalTransships.includes(o.id));
      setSelectedPhysicalOptions(selected);
    } else {
      setSelectedPhysicalOptions([]);
    }
  }, [selectedPhysicalTransships, physicalOptions]);

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = event.target.value;
    setSelectedDate(newDate);

    if (newDate !== "custom") {
      setDateRange([null, null]);
      const todayDate = formatDate(today);
      setPendingParams((prev: any) => ({
        ...prev,
        startDeliveryDate: newDate,
        endDeliveryDate: todayDate, // endDate sempre é hoje para períodos predefinidos
        deliveryDate: newDate,
        page: 1
      }));
    }
  };

  const handleDateRangeChange = (newDateRange: DateRange<Dayjs>) => {
    setDateRange(newDateRange);

    if (newDateRange[0] && newDateRange[1]) {
      setPendingParams((prev: any) => ({
        ...prev,
        startDeliveryDate: newDateRange[0]!.format("YYYY-MM-DD"),
        endDeliveryDate: newDateRange[1]!.format("YYYY-MM-DD"),
        deliveryDate: newDateRange[0]!.format("YYYY-MM-DD"),
        page: 1
      }));
    }
  };

  const handlePhysicalTransshipChange = (_: any, newValue: PhysicalTransship[]) => {
    setSelectedPhysicalOptions(newValue);
    const ids = newValue.map(opt => opt.id);
    setSelectedPhysicalTransships(ids);
    setPendingParams((prev: any) => ({
      ...prev,
      physicalTransshipIds: ids,
      page: 1
    }));
  };

  const handleLocalCheckboxChange = (checkboxId: string) => {
    setLocalCheckboxStates(prev => ({
      ...prev,
      [checkboxId]: !prev[checkboxId]
    }));
  };

  const handleApplyFilters = () => {
    // Inclui todos os parâmetros pendentes, inclusive checkboxes
    const finalParams = {
      ...pendingParams,
      includeSupportDrivers: localCheckboxStates['includeSupportDrivers'] ?? true,
    };
    onChangeParams(finalParams);
    setOpen(false);
  };

  const handleClearFilters = () => {
    const todayDate = formatDate(today);
    const clearedParams = {
      startDeliveryDate: todayDate,
      endDeliveryDate: todayDate,
      deliveryDate: todayDate,
      physicalTransshipIds: [],
      includeSupportDrivers: true,
      page: 1
    };

    setSelectedDate(todayDate);
    setDateRange([null, null]);
    setSelectedPhysicalTransships([]);
    setLocalCheckboxStates({ includeSupportDrivers: true });
    setPendingParams(clearedParams);
  };

  return (
    <Drawer
      open={open}
      onClose={() => setOpen(false)}
      title="Filtros"
      onApply={handleApplyFilters}
      onClear={handleClearFilters}
      buttonsDisabled={loading}
    >
      <FilterSection>
        <SectionTitle>Período</SectionTitle>
        <RadioGroup value={selectedDate} onChange={handleRadioChange}>
          {initialEntries.map((item) => (
            <CustomRadioButton
              key={item.id}
              text={item.name}
              value={item.query}
              checked={selectedDate === item.query}
              handleChange={handleRadioChange}
              size="small"
            />
          ))}
        </RadioGroup>

        {selectedDate === "custom" && (
          <CustomDateSection>
            <SectionTitle>Período Customizado</SectionTitle>
            <DateRangePicker
              value={dateRange}
              onChange={handleDateRangeChange}
              label="Selecione o período"
              placeholder="Escolha as datas"
              maxDays={14}
            />
          </CustomDateSection>
        )}
      </FilterSection>

      <InputSection>
        <SectionTitle>Pontos de transbordo</SectionTitle>
        <Autocomplete
          multiple
          options={physicalOptions}
          value={selectedPhysicalOptions}
          onChange={handlePhysicalTransshipChange}
          getOptionLabel={(option) => {
            const name = (option?.name || "").trim();
            if (name) return name;
            return option?.initials || option?.code || String(option?.id);
          }}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          disableCloseOnSelect
          loading={internalLoading || loading}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option.id}
                label={(option.name?.trim() || option.initials || option.code || String(option.id))}
                size="small"
                sx={{
                  backgroundColor: '#89BD23',
                  color: 'white',
                  '& .MuiChip-deleteIcon': {
                    color: 'white',
                    '&:hover': { color: '#f0f0f0' }
                  }
                }}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={selectedPhysicalOptions.length === 0 ? "Selecione os transbordos físicos" : ""}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {(internalLoading || loading) ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </InputSection>

      {checkboxes && checkboxes.length > 0 && (
        <FilterSection>
          <SectionTitle>Opções</SectionTitle>
          <CheckboxSection>
            {checkboxes.map((checkbox) => (
              <CustomCheckBox
                key={checkbox.id}
                text={checkbox.text}
                checked={localCheckboxStates[checkbox.id] ?? checkbox.state}
                handleToggle={() => handleLocalCheckboxChange(checkbox.id)}
                size="small"
              />
            ))}
          </CheckboxSection>
        </FilterSection>
      )}
    </Drawer>
  );
}
